/**
 * Blog search ranking.
 *
 * The index used to filter with a single `includes()` across title, excerpt,
 * tags and categories, then show the survivors newest-first. Because the
 * corpus publishes several posts a day, "newest" nearly always won: searching
 * "link building" returned an article whose only connection was a tag, ahead
 * of every article with the phrase in its title.
 *
 * Ranking by where the match landed fixes that without changing the corpus.
 * Kept pure so it is testable without a DOM and reusable by any surface that
 * grows a search box.
 */

import { postExcerpt } from "./blogText";

export interface SearchablePost {
  slug: string;
  title: string;
  excerpt?: string;
  metaDescription?: string;
  tags: string[];
  categories: string[];
}

/** Where a query matched, most significant first. Higher scores rank higher. */
export const MATCH_SCORE = {
  titleExact: 100,
  titlePrefix: 80,
  titleWord: 60,
  titleSubstring: 45,
  summary: 25,
  tag: 15,
  category: 10,
} as const;

/**
 * Lower-case, collapse spaces, and fold UK spellings into US ones. Posts are
 * written in US English, but UK readers search "optimisation"; folding both
 * the query and the text makes either spelling find the post.
 */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\b(\w*?)(optim|organ|priorit|recogn|real|maxim|minim|util|custom|central|standard|categor|summar)is(e|ed|es|ing|ation|ations)\b/g, "$1$2iz$3")
    .replace(/\banalys(e|ed|ing)\b/g, "analyz$1")
    .replace(/(behavi|col|fav|hon|lab)our/g, "$1or")
    .replace(/\bcentre/g, "center")
    .replace(/\bprogramme/g, "program")
    .replace(/\bcatalogue/g, "catalog")
    .trim();
}

/** True when `haystack` contains `needle` on a word boundary. */
function containsWord(haystack: string, needle: string): boolean {
  const idx = haystack.indexOf(needle);
  if (idx === -1) return false;
  const before = idx === 0 ? " " : haystack[idx - 1];
  return !/[a-z0-9]/.test(before);
}

/**
 * Relevance of one post to one already-normalised query. 0 means no match,
 * and the post should be filtered out rather than ranked last.
 */
export function scorePost(post: SearchablePost, query: string): number {
  const q = normalise(query);
  if (!q) return 0;

  const title = normalise(post.title);
  if (title === q) return MATCH_SCORE.titleExact;
  if (title.startsWith(q)) return MATCH_SCORE.titlePrefix;
  if (containsWord(title, q)) return MATCH_SCORE.titleWord;
  if (title.includes(q)) return MATCH_SCORE.titleSubstring;

  const summary = normalise(postExcerpt(post));
  if (summary.includes(q)) return MATCH_SCORE.summary;

  if (post.tags.some((t) => normalise(t).includes(q))) return MATCH_SCORE.tag;
  if (post.categories.some((c) => normalise(c.replace(/-/g, " ")).includes(q))) return MATCH_SCORE.category;

  return 0;
}

/**
 * Matching posts, best match first. Ties keep the order they arrived in, so
 * passing a date-sorted list gives "most relevant, then newest" for free.
 */
export function searchPosts<T extends SearchablePost>(posts: T[], query: string): T[] {
  const q = normalise(query);
  if (!q) return posts;
  return posts
    .map((post, index) => ({ post, index, score: scorePost(post, q) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.post);
}

/** Human label for where a result matched, shown under a search result. */
export function matchReason(post: SearchablePost, query: string): string {
  const score = scorePost(post, query);
  if (score >= MATCH_SCORE.titleSubstring) return "";
  if (score === MATCH_SCORE.summary) return "Matches the summary";
  if (score === MATCH_SCORE.tag) return "Matches a tag";
  if (score === MATCH_SCORE.category) return "Matches the topic";
  return "";
}
